import { RestrictToVerticalAxis } from '@dnd-kit/abstract/modifiers';
import { DragDropProvider, type DragEndEvent, type DragOverEvent } from '@dnd-kit/react';
import { isSortable, useSortable } from '@dnd-kit/react/sortable';
import { DragBarOutlined, NextTable, type NextTableProps } from '@fx-ui/fine-design';
import React from 'react';

/**
 * dnd-kit 支持的行唯一标识类型。
 */
type RowKeyValue = string | number;

/**
 * SortableTable 接受的数据行类型。
 */
type TableRowData = Record<string, any>;

/**
 * SortableTable 透传的 NextTable 实例类型。
 */
export interface SortableTableRef {
    /** 表格内容区域。 */
    readonly body: HTMLDivElement;

    /** 表格根节点。 */
    readonly root: HTMLDivElement;

    /** 滚动到指定的纵向位置。 */
    scrollTop(top: number): void;

    /** 滚动到指定的横向位置。 */
    scrollLeft(left: number): void;
}

/**
 * 拖拽触发方式。
 *
 * - handle-left：左侧显示拖拽手柄
 * - row：整行均可触发拖拽
 * - handle-right：右侧显示拖拽手柄
 */
export type SortableTableDragTrigger = 'handle-left' | 'row' | 'handle-right';

/**
 * 拖拽完成后的附加信息。
 */
export interface SortableTableSortInfo<Row> {
    /**
     * 被拖拽的行，即拖拽前 oldIndex 位置的行。
     */
    activeRow: Row;

    /**
     * 拖拽目标位置原本对应的行。该数据取自拖拽开始前的 data[newIndex]。
     */
    overRow: Row;

    /**
     * 拖拽前在完整 data 中的索引。
     */
    oldIndex: number;

    /**
     * 拖拽后在完整 data 中的索引。
     */
    newIndex: number;
}

/**
 * SortableTable 属性。
 *
 * 除了新增的拖拽配置外，其余属性均继承自 NextTable。
 */
export type SortableTableProps<Row extends TableRowData> = Omit<
    NextTableProps<Row, RowKeyValue>,
    'data' | 'rowKey'
> & {
    /**
     * 表格数据。
     *
     * SortableTable 按受控组件使用。拖拽完成后，
     * 使用方需要在 onSortEnd 中更新外部 data。
     */
    data: readonly Row[];

    /**
     * 数据行的唯一标识字段。
     *
     * 对应字段的值必须是 string 或 number，并且不能重复。
     */
    rowKey: Extract<keyof Row, string | number>;

    /**
     * 是否启用行拖拽。
     *
     * @default false
     */
    enableRowDrag?: boolean;

    /**
     * 固定顶部多少行。
     *
     * 固定行：
     *
     * 1. 不可拖拽
     * 2. 不显示拖拽手柄
     * 3. 不会成为拖拽落点
     * 4. 不会被其他行挤动
     *
     * 注意：这里的“固定”是固定排序位置，不是滚动吸顶。
     *
     * @default 0
     */
    fixedRowCount?: number;

    /**
     * 拖拽触发方式。
     *
     * @default 'handle-left'
     */
    dragTrigger?: SortableTableDragTrigger;

    /**
     * 拖拽完成后的回调。
     *
     * 使用方应在此回调中更新外部 data：
     *
     * onSortEnd={(nextData) => setData(nextData)}
     */
    onSortEnd?: (nextData: Row[], info: SortableTableSortInfo<Row>) => void;
};

/**
 * NextTable 生成的行 DOM 选择器。
 */
const ROW_SELECTOR = '[role="row"]';

/**
 * 左右手柄列宽固定为 40px。
 */
const DRAG_HANDLE_COLUMN_WIDTH = 40;

/**
 * 移动数组中的一个元素。
 */
function moveItem<T>(items: readonly T[], fromIndex: number, toIndex: number): T[] {
    if (
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= items.length ||
        toIndex >= items.length ||
        fromIndex === toIndex
    ) {
        return [...items];
    }

    const nextItems = [...items];
    const [movedItem] = nextItems.splice(fromIndex, 1);

    nextItems.splice(toIndex, 0, movedItem!);

    return nextItems;
}

/**
 * 从定位节点向上查找 NextTable 的真实行 DOM。
 */
function getTableRowElement(node: Element | null): Element | null {
    return node?.closest(ROW_SELECTOR) ?? null;
}

/**
 * 用于把 useSortable 产生的 handleRef 传给手柄单元格。
 *
 * 整行拖拽模式下不需要 handleRef。
 */
const SortableRowHandleContext = React.createContext<
    ((element: Element | null) => void) | undefined
>(undefined);

interface SortableRowProps {
    children: React.ReactNode;

    /**
     * 行唯一标识。
     */
    id: RowKeyValue;

    /**
     * 行在“可拖拽数据”中的索引。
     *
     * 这里不包含固定行。
     */
    sortableIndex: number;

    /**
     * 拖拽触发方式。
     */
    trigger: SortableTableDragTrigger;
}

/**
 * 可拖拽行。
 *
 * 固定行不会渲染该组件，也就不会注册进 dnd-kit。
 */
const SortableRow = ({ children, id, sortableIndex, trigger }: SortableRowProps) => {
    const { ref, handleRef, isDragging } = useSortable({
        id,
        index: sortableIndex
    });

    const rowElementRef = React.useRef<Element | null>(null);
    const useHandle = trigger !== 'row';

    /**
     * hidden span 会被 NextTable 渲染到真实行 DOM 内，
     * 因此可以通过 closest 找到 role="row" 元素。
     */
    const bindRowElement = React.useCallback(
        (node: HTMLSpanElement | null) => {
            const rowElement = getTableRowElement(node);

            rowElementRef.current = rowElement;
            ref(rowElement);
        },
        [ref]
    );

    /**
     * 为整行拖拽补充鼠标样式，并提高拖拽行层级。
     */
    React.useLayoutEffect(() => {
        const rowElement = rowElementRef.current;

        if (!(rowElement instanceof HTMLElement)) {
            return;
        }

        const previousCursor = rowElement.style.cursor;
        const previousZIndex = rowElement.style.zIndex;

        rowElement.style.zIndex = isDragging ? '1' : '';

        if (!useHandle) {
            rowElement.style.cursor = isDragging ? 'grabbing' : 'grab';
        }

        return () => {
            rowElement.style.cursor = previousCursor;
            rowElement.style.zIndex = previousZIndex;
        };
    }, [isDragging, useHandle]);

    return (
        <SortableRowHandleContext.Provider value={useHandle ? handleRef : undefined}>
            {/*
             * 仅用于定位真实行 DOM，不参与页面布局。
             */}
            <span ref={bindRowElement} hidden />

            {children}
        </SortableRowHandleContext.Provider>
    );
};

/**
 * 行拖拽手柄。
 */
const DragHandle = () => {
    const handleRef = React.useContext(SortableRowHandleContext);

    return (
        <span
            ref={handleRef}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'grab'
            }}
        >
            <DragBarOutlined />
        </span>
    );
};

/**
 * SortableTable 主组件。
 */
const SortableTableInner = React.forwardRef(
    <Row extends TableRowData>(
        props: SortableTableProps<Row>,
        ref: React.ForwardedRef<SortableTableRef>
    ) => {
        const {
            data,
            rowKey,
            enableRowDrag = false,
            fixedRowCount = 0,
            dragTrigger = 'handle-left',
            onSortEnd,
            children,
            renderRow: customRenderRow,
            shouldUpdateScroll,
            ...tableProps
        } = props;

        /**
         * NextTable 没有从包入口导出实例类型，因此通过 callback ref
         * 将其实例收窄为 SortableTable 对外承诺的公开能力。
         */
        const bindTableRef = React.useCallback(
            (instance: SortableTableRef | null) => {
                if (typeof ref === 'function') {
                    ref(instance);
                } else if (ref) {
                    ref.current = instance;
                }
            },
            [ref]
        );

        /**
         * 拖拽过程中使用内部数据实时改变页面顺序。
         *
         * 最终顺序通过 onSortEnd 交给外部。
         */
        const [innerData, setInnerData] = React.useState<Row[]>(() => [...data]);

        /**
         * 保存当前最新数据，避免拖拽事件读取到旧 state。
         */
        const dataRef = React.useRef<Row[]>(innerData);

        /**
         * 保存一次拖拽开始前的数据。
         *
         * 拖拽取消时用于恢复。
         */
        const dragSnapshotRef = React.useRef<Row[]>(innerData);

        /**
         * 标记当前是否正在拖拽。
         *
         * 使用 ref 可以避免仅用于同步控制的状态触发额外渲染。
         */
        const isDraggingRef = React.useRef(false);

        /**
         * 始终保存最新的外部数据。
         *
         * 拖拽取消后，需要恢复到最新的外部数据，而不是可能已经过期的拖拽前快照。
         */
        const externalDataRef = React.useRef<readonly Row[]>(data);

        externalDataRef.current = data;

        /**
         * 外部 data 变化时，同步内部数据。
         *
         * 拖拽期间暂停同步，避免外部数据覆盖正在进行的内部排序。
         */
        React.useEffect(() => {
            if (isDraggingRef.current) {
                return;
            }

            const nextData = [...data];

            dataRef.current = nextData;
            setInnerData(nextData);
        }, [data]);

        /**
         * 将 fixedRowCount 约束到合法范围。
         */
        const normalizedFixedRowCount = Math.min(
            Math.max(Math.floor(fixedRowCount), 0),
            innerData.length
        );

        /**
         * 获取数据行唯一标识。
         *
         * dnd-kit 要求 id 必须是 string 或 number。
         */
        const getRowId = React.useCallback(
            (row: Row): RowKeyValue => {
                const value = row[rowKey];
                return value;
            },
            [rowKey]
        );

        /**
         * 固定行 ID 集合。
         *
         * 用于判断手柄列是否需要展示手柄。
         */
        const fixedRowIds = React.useMemo(() => {
            return new Set<RowKeyValue>(innerData.slice(0, normalizedFixedRowCount).map(getRowId));
        }, [getRowId, innerData, normalizedFixedRowCount]);

        /**
         * 计算可拖拽行的索引。
         *
         * 这里排除了固定行，并从 0 重新编号。
         * 因此固定行不会进入 dnd-kit 的排序列表。
         */
        const sortableIndexMap = React.useMemo(() => {
            return new Map<RowKeyValue, number>(
                innerData.slice(normalizedFixedRowCount).map((row, index) => [getRowId(row), index])
            );
        }, [getRowId, innerData, normalizedFixedRowCount]);

        /**
         * 根据拖拽事件计算新的数据顺序。
         *
         * 数据被拆分为固定区域和可拖拽区域，
         * 排序只发生在可拖拽区域。
         */
        const applySort = React.useCallback(
            (event: DragOverEvent | DragEndEvent): Row[] => {
                const { source } = event.operation;

                if (!isSortable(source)) {
                    return dataRef.current;
                }

                const currentData = dataRef.current;

                const safeFixedCount = Math.min(
                    Math.max(Math.floor(fixedRowCount), 0),
                    currentData.length
                );

                /**
                 * 固定区域始终保持不变。
                 */
                const fixedRows = currentData.slice(0, safeFixedCount);

                /**
                 * 只有该区域参与排序。
                 */
                const sortableRows = currentData.slice(safeFixedCount);

                const fromIndex = sortableRows.findIndex((row) => getRowId(row) === source.id);

                if (fromIndex < 0 || sortableRows.length === 0) {
                    return currentData;
                }

                /**
                 * source.index 是可拖拽列表中的目标位置。
                 */
                const toIndex = Math.min(Math.max(source.index, 0), sortableRows.length - 1);

                if (fromIndex === toIndex) {
                    return currentData;
                }

                const nextData = [...fixedRows, ...moveItem(sortableRows, fromIndex, toIndex)];

                dataRef.current = nextData;
                setInnerData(nextData);

                return nextData;
            },
            [fixedRowCount, getRowId]
        );

        /**
         * 拖拽开始时保存当前顺序。
         */
        const handleDragStart = React.useCallback(() => {
            isDraggingRef.current = true;
            dragSnapshotRef.current = [...dataRef.current];
        }, []);

        /**
         * 拖拽经过其他行时实时更新顺序。
         */
        const handleDragOver = React.useCallback(
            (event: DragOverEvent) => {
                applySort(event);
            },
            [applySort]
        );

        /**
         * 拖拽完成。
         */
        const handleDragEnd = React.useCallback(
            (event: DragEndEvent) => {
                /**
                 * 无论拖拽是否取消或事件是否合法，
                 * 拖拽结束后都需要恢复外部数据同步。
                 */
                isDraggingRef.current = false;

                /**
                 * 取消拖拽时恢复最新的外部数据，
                 * 并且不触发 onSortEnd。
                 */
                if (event.canceled) {
                    const externalData = [...externalDataRef.current];

                    dataRef.current = externalData;
                    setInnerData(externalData);
                    return;
                }

                const { source } = event.operation;

                if (!isSortable(source)) {
                    return;
                }

                /**
                 * 再应用一次最终位置，避免最后一次
                 * dragOver 没有触发导致结果不准确。
                 */
                const nextData = applySort(event);
                const previousData = dragSnapshotRef.current;

                const oldIndex = previousData.findIndex((row) => getRowId(row) === source.id);

                const newIndex = nextData.findIndex((row) => getRowId(row) === source.id);

                if (oldIndex < 0 || newIndex < 0) {
                    return;
                }

                /**
                 * activeRow 是被拖拽行。
                 * overRow 是目标位置在拖拽前对应的行。
                 */
                const activeRow = previousData[oldIndex]!;
                const overRow = previousData[newIndex]!;

                onSortEnd?.([...nextData], {
                    activeRow,
                    overRow,
                    oldIndex,
                    newIndex
                });
            },
            [applySort, getRowId, onSortEnd]
        );

        /**
         * 合并调用方的 renderRow 和拖拽包装。
         */
        const renderSortableRow = React.useCallback(
            (rowChildren?: React.ReactNode, rowData?: Row): React.ReactNode => {
                /**
                 * 优先执行调用方传入的 renderRow。
                 */
                const renderedRow = customRenderRow
                    ? customRenderRow(rowChildren, rowData)
                    : rowChildren;

                if (!enableRowDrag || !rowData) {
                    return renderedRow;
                }

                const id = getRowId(rowData);
                const sortableIndex = sortableIndexMap.get(id);

                /**
                 * 找不到 sortableIndex，说明这是固定行。
                 */
                if (sortableIndex === undefined) {
                    return renderedRow;
                }

                return (
                    <SortableRow id={id} sortableIndex={sortableIndex} trigger={dragTrigger}>
                        {renderedRow}
                    </SortableRow>
                );
            },
            [customRenderRow, dragTrigger, enableRowDrag, getRowId, sortableIndexMap]
        );

        /**
         * 创建固定宽度为 40px 的手柄列。
         *
         * 固定行不展示手柄。
         */
        const dragHandleColumn = (
            <NextTable.Column
                width={DRAG_HANDLE_COLUMN_WIDTH}
                align="center"
                verticalAlign="middle"
            >
                <NextTable.HeaderCell>{null}</NextTable.HeaderCell>

                <NextTable.Cell>
                    {(rowData: Row) => {
                        const id = getRowId(rowData);

                        if (fixedRowIds.has(id)) {
                            return null;
                        }

                        return <DragHandle />;
                    }}
                </NextTable.Cell>
            </NextTable.Column>
        );

        /**
         * 实际的 NextTable。
         *
         * children 使用函数形式，是为了同时兼容：
         *
         * 1. 普通 JSX 列定义
         * 2. NextTable 原本支持的 children 函数
         */
        const table = (
            <NextTable
                {...tableProps}
                ref={bindTableRef}
                data={innerData}
                rowKey={rowKey}
                renderRow={renderSortableRow}
                shouldUpdateScroll={shouldUpdateScroll ?? (enableRowDrag ? false : undefined)}
            >
                {(components) => (
                    <>
                        {enableRowDrag && dragTrigger === 'handle-left' ? dragHandleColumn : null}

                        {typeof children === 'function' ? children(components) : children}

                        {enableRowDrag && dragTrigger === 'handle-right' ? dragHandleColumn : null}
                    </>
                )}
            </NextTable>
        );

        /**
         * 未启用拖拽时不挂载 dnd-kit Provider。
         */
        if (!enableRowDrag) {
            return table;
        }

        return (
            <DragDropProvider
                modifiers={[RestrictToVerticalAxis]}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
            >
                {table}
            </DragDropProvider>
        );
    }
);

/**
 * 保留 NextTable 的静态子组件调用方式：
 *
 * SortableTable.Column
 * SortableTable.Cell
 * SortableTable.HeaderCell
 * SortableTable.ColumnGroup
 */
interface SortableTableComponent {
    <Row extends TableRowData>(
        props: SortableTableProps<Row> & React.RefAttributes<SortableTableRef>
    ): React.ReactElement | null;

    Cell: typeof NextTable.Cell;
    Column: typeof NextTable.Column;
    HeaderCell: typeof NextTable.HeaderCell;
    ColumnGroup: typeof NextTable.ColumnGroup;
}

const SortableTable = Object.assign(SortableTableInner, {
    Cell: NextTable.Cell,
    Column: NextTable.Column,
    HeaderCell: NextTable.HeaderCell,
    ColumnGroup: NextTable.ColumnGroup
}) as SortableTableComponent;

export default SortableTable;
